import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { Button, BackButton, LoadingButton, useToast } from '../../components/ui';

function ContentGeneratorThreads() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedThreadsKeyword, setSelectedThreadsKeyword] = useState('');
  const [threadsSearchResults, setThreadsSearchResults] = useState([]);
  const [isSearchingThreads, setIsSearchingThreads] = useState(false);
  const [selectedPostIndex, setSelectedPostIndex] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);

  const searchThreadsKeyword = async (keyword) => {
    if (!keyword.trim()) {
      toast.warning('Please enter a keyword');
      return;
    }

    setSelectedThreadsKeyword(keyword);
    setIsSearchingThreads(true);
    setThreadsSearchResults([]);
    setSelectedPostIndex(null);

    try {
      const token = localStorage.getItem('token');

      // Create project first
      const createResponse = await fetch('/api/projects/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'content-generator',
          source: 'keyword-threads',
          currentStep: 1,
          keywords: [keyword],
          topic: keyword,
          pageCount: 5,
          contentType: 'carousel',
          language: 'zh-TW',
          preset: 'default',
          generationStatus: 'draft'
        })
      });

      const createResult = await createResponse.json();
      if (!createResult.success) {
        toast.error('Failed to create project');
        setIsSearchingThreads(false);
        return;
      }

      const projectId = createResult.project.id;
      setCurrentProjectId(projectId);

      // Search Threads
      const params = new URLSearchParams({
        q: keyword,
        search_type: 'TOP',
        search_mode: 'KEYWORD',
        limit: 15
      });

      const response = await fetch(`/api/threads-keyword-search/search?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setThreadsSearchResults(data.data || []);
          if (data.data && data.data.length > 0) {
            toast.success(`Found ${data.totalMatches || data.data.length} posts for "${keyword}"`);
          } else {
            toast.info(`No posts found for "${keyword}"`);
          }
        } else {
          toast.error(data.message || 'Search failed');
        }
      }
    } catch (error) {
      console.error('Failed to search Threads:', error);
      toast.error('Failed to search Threads posts');
    } finally {
      setIsSearchingThreads(false);
    }
  };

  const handleSelectThreadsPost = async (index, post) => {
    setSelectedPostIndex(index);
    if (!currentProjectId) {
      toast.error('請先搜尋關鍵字');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${currentProjectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          referencePost: post,
          currentStep: 2
        })
      });

      const result = await response.json();
      if (!result.success) {
        toast.error('Failed to update project');
        return;
      }

      toast.success('已選擇貼文，進入下一步');
      navigate(`/templates/content-generator/step2?projectId=${currentProjectId}`);
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('Failed to update project');
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      {/* Home Button */}
      <Button
        onClick={() => navigate('/home')}
        variant="primary"
        size="md"
        icon="mdi:home"
        className="fixed top-8 left-8 z-[100]"
      >
        Home
      </Button>

      {/* Back Button */}
      <BackButton
        onClick={() => navigate('/templates/content-generator')}
        className="fixed bottom-8 left-8 z-[100]"
      >
        Back
      </BackButton>

      {/* Main Content */}
      <div className="max-w-[980px] w-full bg-white p-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-purple-50 rounded-2xl flex items-center justify-center">
            <Icon icon="mdi:forum-outline" className="text-4xl text-purple-500" />
          </div>
          <h1 className="text-4xl font-bold text-black mb-2">Threads Reference</h1>
          <p className="text-base text-gray-500">Find trending posts to inspire your content</p>
        </div>

        {/* Search Section */}
        <div className="max-w-[800px] mx-auto">
          <div className="text-sm font-semibold text-slate-800 mb-4">Search Threads</div>
          <div className="flex gap-3 mb-6">
            <Input
              size="large"
              placeholder="Enter keyword or topic..."
              value={selectedThreadsKeyword}
              onChange={(e) => setSelectedThreadsKeyword(e.target.value)}
              onPressEnter={() => searchThreadsKeyword(selectedThreadsKeyword)}
              disabled={isSearchingThreads}
              prefix={<Icon icon="mdi:magnify" className="text-slate-400 text-xl" />}
              className="flex-1 rounded-lg"
            />
            <LoadingButton
              onClick={() => searchThreadsKeyword(selectedThreadsKeyword)}
              disabled={!selectedThreadsKeyword.trim()}
              loading={isSearchingThreads}
              loadingText="Searching..."
              icon="mdi:send"
              variant="primary"
              size="lg"
            >
              Search
            </LoadingButton>
          </div>

          {/* Search Results */}
          {!isSearchingThreads && threadsSearchResults.length > 0 && (
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-4">
                Search Results ({threadsSearchResults.length} posts)
              </div>
              <div className="text-xs text-slate-500 mb-4">
                Click on a post to use it as reference
              </div>
              <div className="max-h-[400px] overflow-y-auto grid grid-cols-3 gap-3">
                {threadsSearchResults.map((post, index) => {
                  const isSelected = selectedPostIndex === index;
                  return (
                    <div
                      key={index}
                      onClick={() => handleSelectThreadsPost(index, post)}
                      className={`p-4 rounded-lg cursor-pointer transition-all hover:-translate-y-0.5 ${
                        isSelected
                          ? 'bg-blue-50 border border-blue-500 shadow-[0_0_0_3px_rgba(33,150,243,0.1)]'
                          : 'bg-gray-50 border border-gray-200 hover:border-black hover:bg-white'
                      }`}
                    >
                      <div className="text-[11px] text-slate-500 mb-2 flex gap-4 items-center">
                        <span className="flex items-center gap-1">
                          <Icon icon="mdi:heart" className="text-sm text-red-500" />
                          {post.likes || "—"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon icon="mdi:comment-outline" className="text-sm text-slate-500" />
                          {post.replies || 0}
                        </span>
                      </div>
                      <div className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap line-clamp-4">
                        {post.text || post.content || 'No content'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isSearchingThreads && threadsSearchResults.length === 0 && selectedThreadsKeyword && (
            <div className="text-center py-12 text-slate-400">
              <Icon icon="mdi:forum-outline" className="text-5xl mb-4 opacity-50" />
              <p>No posts found. Try a different keyword.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContentGeneratorThreads;
